/**
 * Composable for checking if a hotel has existing orders/reservations
 * Used to determine if supplier changes should be disabled
 */
export const useHotelOrdersCheck = () => {
    const app = useNuxtApp()
    const authStore = useAuthStore()
    const isCheckingOrders = ref(false)
    const hasOrders = ref(false)

    /**
     * Check if a hotel has existing orders/reservations
     * @param productId - The product/contract ID to check (should be contract ID, not hotel description ID)
     * @returns Promise<boolean> - true if hotel has orders, false otherwise
     */
    const checkHotelOrders = async (productId: number | string): Promise<boolean> => {
        if (!productId) {
            hasOrders.value = false
            return false
        }

        try {
            isCheckingOrders.value = true

            // Prepare request parameters to check for orders
            // Based on the correct API structure: /api/orders?orderType=TO&responseType=SHORT&limit=1&offset=0&filterServices=true&active=true&productId=1713783
            const requestParams: any = {
                orderType: authStore.orderType, // Required parameter (TO, TA, SUPPLIER)
                responseType: 'SHORT',
                limit: 1, // We only need to know if any orders exist
                offset: 0,
                filterServices: true, // Filter by services
                active: true, // Only active orders
                productId: productId, // Use productId parameter for hotel identification
            }

            // Note: supplierId is not added to the request as it's not needed for this check

            const response = await app.$api.orders.get(requestParams)

            // Check for orders using multiple indicators from the API response
            const ordersFound =
                (response?.orders && response.orders.length > 0) || (response?.ordersCount && response.ordersCount > 0)

            hasOrders.value = ordersFound
            return ordersFound
        } catch (error) {
            console.error('Error checking hotel orders:', error)
            // In case of error, assume no orders to allow editing
            // This is a conservative approach to avoid blocking legitimate edits
            hasOrders.value = false
            return false
        } finally {
            isCheckingOrders.value = false
        }
    }

    /**
     * Reset the orders check state
     */
    const resetOrdersCheck = () => {
        hasOrders.value = false
        isCheckingOrders.value = false
    }

    return {
        isCheckingOrders: readonly(isCheckingOrders),
        hasOrders: readonly(hasOrders),
        checkHotelOrders,
        resetOrdersCheck,
    }
}
